import React, { useState, useEffect } from "react";
import NavigationMenu from "./NavigationMenu";
import CustomConfirmModal from "./CustomConfirmModal";
import { cleanBankTitle } from "../utils/bankTitle";

function ResumeExamsView({
  savedSessions = [],
  onResumeSession,
  onDeleteSession,
  onNavigate,
  candidateName,
  currentUser,
  onOpenAuth,
  onLogout,
}) {
  const [confirmDialog, setConfirmDialog] = useState({
    isOpen: false,
    title: "",
    message: "",
    confirmText: "Delete",
    cancelText: "Cancel",
    type: "danger",
    onConfirm: () => {},
  });

  useEffect(() => {
    window.scrollTo({ top: 0, left: 0, behavior: "instant" });
    document.documentElement.scrollTop = 0;
    document.body.scrollTop = 0;
  }, []);

  const formatRelativeTime = (timestamp, fallbackSessionId) => {
    let effective = timestamp;
    if (!effective && typeof fallbackSessionId === "string" && fallbackSessionId.startsWith("session_")) {
      const parsed = parseInt(fallbackSessionId.replace("session_", ""), 10);
      if (!isNaN(parsed) && parsed > 1000000000000) {
        effective = parsed;
      }
    }
    if (!effective) return "Just now";
    const ts = typeof effective === "string" ? new Date(effective).getTime() : Number(effective);
    if (!ts || isNaN(ts)) return "Just now";
    const numTs = ts < 1e11 ? ts * 1000 : ts;
    const diffSec = Math.max(0, Math.floor((Date.now() - numTs) / 1000));
    if (diffSec < 60) return "Just now";
    const diffMin = Math.floor(diffSec / 60);
    if (diffMin < 60) return `${diffMin} min${diffMin > 1 ? "s" : ""} ago`;
    const diffHours = Math.floor(diffMin / 60);
    if (diffHours < 24) return `${diffHours} hour${diffHours > 1 ? "s" : ""} ago`;
    const diffDays = Math.floor(diffHours / 24);
    return `${diffDays} day${diffDays > 1 ? "s" : ""} ago`;
  };

  const handleDeleteSessionClick = (session) => {
    const bank = cleanBankTitle(session.selectedBankName || session.bankName || "Exam A");
    setConfirmDialog({
      isOpen: true,
      title: "Discard Saved Exam Session?",
      message: `Are you sure you want to delete and discard your saved progress for "${bank}"? This action cannot be undone.`,
      confirmText: "Discard Session",
      cancelText: "Cancel",
      type: "danger",
      onConfirm: () => {
        onDeleteSession(session.id);
        setConfirmDialog((prev) => ({ ...prev, isOpen: false }));
      },
    });
  };

  const validSessions = (savedSessions || []).filter((s) => {
    if (!s || !s.questions || !Array.isArray(s.questions) || s.questions.length === 0) return false;
    if (s.status === "finished" || s.isFinished) return false;
    const answersList = Array.isArray(s.answers) ? s.answers : [];
    const answeredCount = answersList.filter((a) => a !== null && a !== undefined && a !== "").length;
    if (answeredCount >= s.questions.length && s.questions.length > 0) return false;
    return true;
  });

  return (
    <div className="resume-exams-page-container">
      <NavigationMenu
        currentView="resume-exams"
        onNavigate={onNavigate}
        candidateName={candidateName}
        currentUser={currentUser}
        onOpenAuth={onOpenAuth}
        onLogout={onLogout}
        pageTitle="My In-Progress Exams"
      />

      <div className="resume-exams-content">
        {validSessions.length === 0 ? (
          <div className="empty-sessions-card">
            <span className="empty-icon">📂</span>
            <h3>No in-progress exams found</h3>
            <p>Start a new exam from the Home Dashboard to track your progress here.</p>
            <button
              type="button"
              className="btn-start-new-exam-home"
              onClick={() => onNavigate("dashboard")}
            >
              + Start a New Exam
            </button>
          </div>
        ) : (
          <div className="resume-sessions-list">
            {validSessions.map((session, index) => {
              const currentQ = (session.index || 0) + 1;
              const totalQ = session.questions?.length || 74;
              const progressPct = Math.min(
                100,
                Math.max(2, (currentQ / (totalQ || 1)) * 100)
              );

              return (
                <div key={session.id || index} className="resume-session-card">
                  <div className="session-card-header">
                    <h3 className="session-exam-title">
                      Cisco 200-301 CCNA
                    </h3>

                    <div className="session-header-right">
                      <span
                        className={`session-badge ${
                          session.examMode === "simulation"
                            ? "badge-sim"
                            : "badge-study"
                        }`}
                      >
                        {session.examMode === "simulation"
                          ? "Simulation Mode"
                          : "Study Mode"}
                      </span>

                      <span className="session-badge badge-bank">
                        {cleanBankTitle(session.selectedBankName || session.bankName || "Exam A")}
                      </span>

                      <button
                        type="button"
                        className="btn-delete-session-icon"
                        onClick={() => handleDeleteSessionClick(session)}
                        title="Delete this saved session"
                      >
                        🗑️
                      </button>
                    </div>
                  </div>

                  <div className="session-meta-lines">
                    <div>
                      Started <strong>{formatRelativeTime(session.startedAt || session.started_at, session.id)}</strong>
                    </div>
                    <div>
                      Last accessed <strong>{formatRelativeTime(session.savedAt || session.updatedAt || session.updated_at)}</strong>
                    </div>
                  </div>

                  <div className="session-progress-track">
                    <div
                      className="session-progress-fill"
                      style={{ width: `${progressPct}%` }}
                    ></div>
                  </div>

                  <div className="session-card-footer">
                    <span className="session-q-count">
                      {currentQ}/{totalQ}
                    </span>

                    <button
                      type="button"
                      className="btn-pickup-resume"
                      onClick={() => onResumeSession(session)}
                    >
                      Resume ➜
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* CONFIRMATION MODAL */}
      <CustomConfirmModal
        isOpen={confirmDialog.isOpen}
        title={confirmDialog.title}
        message={confirmDialog.message}
        confirmText={confirmDialog.confirmText}
        cancelText={confirmDialog.cancelText}
        type={confirmDialog.type}
        onConfirm={confirmDialog.onConfirm}
        onCancel={() => setConfirmDialog((prev) => ({ ...prev, isOpen: false }))}
      />
    </div>
  );
}

export default ResumeExamsView;
